import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import type { EngineController, LiveState } from "../app/controller";
import type { Library } from "../app/library";
import type { PracticeService } from "../app/practice";
import type { TunerService } from "../app/tuner";
import type { SetlistService } from "../app/setlists";
import type { Project } from "../project/schema";

interface Services { controller: EngineController; library: Library; practice: PracticeService; tuner: TunerService; setlists: SetlistService; }

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ value, children }: { value: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const s = useContext(ServicesContext);
  if (!s) throw new Error("ServicesProvider missing");
  return s;
}

/** Subscribe to a slice of the project document. Selectors must return stable references. */
export function useProject<T>(select: (p: Project) => T): T {
  const { controller } = useServices();
  return useSyncExternalStore(controller.project.subscribe, () => select(controller.project.get()));
}

export function useLive<T>(select: (s: LiveState) => T): T {
  const { controller } = useServices();
  return useSyncExternalStore(controller.live.subscribe, () => select(controller.live.get()));
}

export function useDirty(): boolean {
  const { controller } = useServices();
  return useSyncExternalStore(controller.doc.subscribe, () => controller.doc.get().dirty);
}

export function usePractice() {
  const { practice } = useServices();
  return useSyncExternalStore(practice.state.subscribe, practice.state.get);
}

export function useTuner() {
  const { tuner } = useServices();
  return useSyncExternalStore(tuner.state.subscribe, tuner.state.get);
}

export function useSetlists() {
  const { setlists } = useServices();
  return useSyncExternalStore(setlists.state.subscribe, setlists.state.get);
}

export function useHistoryState() {
  const { controller } = useServices();
  return useSyncExternalStore(controller.history.subscribe, controller.history.get);
}

export function useMeters() {
  const { controller } = useServices();
  return useSyncExternalStore(controller.meters.subscribe, controller.meters.get);
}

export function usePosition(): number | null {
  const { controller } = useServices();
  return useSyncExternalStore(controller.position.subscribe, controller.position.get);
}
